// Legal pages — Privacy Policy and Terms of Service.
// Kept in one file so updates stay in sync and imports stay light.
//
// These are deliberately SHORT, plain-language versions for an early launch:
// honest about what we do, without pretending to be a large company with a
// full compliance program. They are not a substitute for legal advice — run
// them past a lawyer in your jurisdiction before relying on them commercially.
//
// When updating: bump EFFECTIVE_DATE, and if changes are material, email
// account admins before they take effect.
//
// ────────────────────────────────────────────────────────────────────────────
// Styling note: apps/website doesn't ship the @tailwindcss/typography plugin.
// The LegalLayout wrapper uses arbitrary-variant child selectors
// ([&_h2]:..., [&_p]:..., etc.) so the policy body can stay as plain semantic
// HTML — no need to rewrite every <h2>/<p>/<ul> as a styled component.

import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';

const EFFECTIVE_DATE = 'April 27, 2026';
const CONTACT_EMAIL = 'support@brixlead.com';
const PRIVACY_EMAIL = 'privacy@brixlead.com';
const DPO_EMAIL = 'info@brixlead.com';
const COMPANY_NAME = 'BrixLead';
// Beta wording — BrixLead is not yet incorporated as a company.
// COMPANY_LEGAL therefore mirrors the brand name, and the address string
// flags the registration-pending state honestly rather than fabricating an
// entity that doesn't exist. When the company is formally registered, swap
// these two constants + set IS_BETA to false — nothing else needs to change.
const COMPANY_LEGAL = 'BrixLead (an unincorporated brand)';
const COMPANY_ADDRESS = 'Incorporation in progress — a mailing address will be published upon registration. Until then, reach us at ' + CONTACT_EMAIL + '.';
// Set to false once the company is registered — hides the beta banner.
const IS_BETA = true;

// Slugify section titles into anchor IDs so the TOC can deep-link.
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// Auto-generated table of contents.
//
// Walks the rendered article DOM after mount and pulls every <h2>, assigns
// it an id, and renders a sidebar list. Reading from the DOM keeps the source
// of truth singular: the headings in the policy text.
function useAutoToc(deps: unknown[]) {
  const [items, setItems] = useState<{ id: string; label: string }[]>([]);

  useEffect(() => {
    const article = document.getElementById('legal-article');
    if (!article) return;
    const headings = Array.from(article.querySelectorAll('h2'));
    const next = headings.map((h) => {
      const label = h.textContent?.trim() ?? '';
      const id = slugify(label);
      h.id = id;
      h.style.scrollMarginTop = '5rem';
      return { id, label };
    });
    setItems(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return items;
}

function LegalLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  // Re-run auto-TOC whenever the title changes, i.e. when the user navigates
  // between Privacy / Terms. children isn't a stable dep, so title is the
  // cheapest signal.
  const toc = useAutoToc([title]);
  const [tocOpen, setTocOpen] = useState(false);

  return (
    <div className="bg-white">
      {/* ── Beta banner (drops out post-incorporation, see IS_BETA) ──── */}
      {IS_BETA && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-4xl mx-auto px-4 py-3 text-center text-sm text-amber-800">
            <b>Private beta.</b> {COMPANY_NAME} is not yet incorporated as a
            company — legal entity registration is in progress. This document
            outlines the commitments we intend to bind ourselves to on
            incorporation. Beta customers get the product free during this
            period. Questions? <a href={`mailto:${CONTACT_EMAIL}`} className="underline font-semibold">{CONTACT_EMAIL}</a>
          </div>
        </div>
      )}
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur px-3 py-1 mb-5 border border-slate-200">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs font-medium text-[#475569]">Legal</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#1E293B] tracking-tight">{title}</h1>
          {subtitle && <p className="mt-4 text-base sm:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
          <p className="mt-5 text-sm text-[#64748B]">Effective date: <span className="font-semibold text-[#475569]">{EFFECTIVE_DATE}</span></p>
        </div>
      </section>

      {/* ── Mobile TOC trigger (sticky bar) ──────────────────────────── */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <button
          onClick={() => setTocOpen((v) => !v)}
          className="w-full max-w-7xl mx-auto px-4 py-3 flex items-center justify-between text-sm font-semibold text-[#1E293B]"
        >
          <span className="flex items-center gap-2">
            {tocOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            On this page
          </span>
          <span className="text-xs text-[#64748B]">{toc.length} sections</span>
        </button>
        {tocOpen && (
          <nav className="max-w-7xl mx-auto px-4 pb-4 max-h-[60vh] overflow-y-auto">
            <ul className="space-y-1">
              {toc.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    onClick={() => setTocOpen(false)}
                    className="block py-2 px-3 rounded-lg text-sm text-[#475569] hover:bg-indigo-50 hover:text-[#6366F1] transition-colors"
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>

      {/* ── Content + sidebar ────────────────────────────────────────── */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-10">
          {/* Sticky sidebar TOC (desktop only) */}
          <aside className="hidden lg:block">
            <div className="sticky top-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-3">On this page</p>
              <nav>
                <ul className="space-y-1 border-l border-slate-200">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        className="block -ml-px pl-4 py-1.5 text-sm text-[#475569] border-l border-transparent hover:border-[#6366F1] hover:text-[#6366F1] transition-colors"
                      >
                        {t.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="mt-6 p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                <p className="text-xs font-semibold text-[#1E293B] mb-1">Questions?</p>
                <p className="text-xs text-[#475569] leading-relaxed">
                  Email <a href={`mailto:${PRIVACY_EMAIL}`} className="text-[#6366F1] font-medium hover:underline">{PRIVACY_EMAIL}</a> and we'll help.
                </p>
              </div>
            </div>
          </aside>

          {/* Article — uses arbitrary-variant child selectors to style all
              the plain HTML inside each policy without needing the
              Tailwind typography plugin. */}
          <article
            id="legal-article"
            className={[
              'min-w-0 text-[#475569] text-[15px] leading-7',
              '[&>p:first-of-type]:text-base [&>p:first-of-type]:text-[#334155] [&>p:first-of-type]:leading-relaxed',
              '[&_h2]:text-2xl [&_h2]:sm:text-[26px] [&_h2]:font-bold [&_h2]:text-[#1E293B] [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:pb-3 [&_h2]:border-b [&_h2]:border-slate-200 [&_h2]:tracking-tight',
              '[&_h2:first-child]:mt-0',
              '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#334155] [&_h3]:mt-7 [&_h3]:mb-2',
              '[&_p]:my-4 [&_p]:leading-7',
              '[&_ul]:my-4 [&_ul]:pl-5 [&_ul]:space-y-2 [&_ul>li]:list-disc [&_ul>li]:pl-1 [&_ul>li]:marker:text-[#6366F1]',
              '[&_li]:leading-7',
              '[&_a]:text-[#6366F1] [&_a]:font-medium [&_a:hover]:underline',
              '[&_b]:font-semibold [&_b]:text-[#1E293B]',
            ].join(' ')}
          >
            {children}

            {/* Footer card — closes every legal page with a consistent CTA. */}
            <div className="mt-16 p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100">
              <p className="text-sm font-semibold text-[#1E293B]">Questions about this policy?</p>
              <p className="text-sm text-[#475569] mt-1.5 leading-relaxed">
                Email <a href={`mailto:${PRIVACY_EMAIL}`} className="text-[#6366F1] font-medium hover:underline">{PRIVACY_EMAIL}</a>{' '}
                for privacy queries, or <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#6366F1] font-medium hover:underline">{CONTACT_EMAIL}</a>{' '}
                for general support. We usually reply within 1 business day.
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

// ── Privacy Policy ──────────────────────────────────────────────────────────

export function PrivacyPolicyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="What we collect, how we use it, and the choices you have. Written to be short and readable."
    >
      <p>
        This Privacy Policy explains how {COMPANY_LEGAL} ({COMPANY_NAME}, "we", "us", "our")
        handles personal data when you visit our website, create an account, or use our
        CRM platform. By using {COMPANY_NAME}, you agree to this policy.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li><b>Account information</b> — your name, work email, password (stored only as a
          salted hash), organization name, country, and phone number.</li>
        <li><b>Data you add</b> — the leads, notes, files, and custom fields you store in
          your workspace. This is your data; we only process it to run the service for you.</li>
        <li><b>Usage &amp; diagnostics</b> — basic information about how the app is used
          (pages, features, device/browser, approximate location from IP) and error reports
          that help us fix bugs.</li>
      </ul>
      <p>
        We use only the essential cookies needed to keep you signed in. We do not set
        advertising or tracking cookies.
      </p>

      <h2>How we use it</h2>
      <ul>
        <li>To provide and operate the platform.</li>
        <li>To send you transactional emails (verification, password reset, billing).</li>
        <li>To keep the service secure and prevent abuse.</li>
        <li>To meet legal and tax obligations.</li>
        <li>To send product updates — only if you opt in, and you can unsubscribe anytime.</li>
      </ul>

      <h2>What we don't do</h2>
      <ul>
        <li>We don't sell your data or your leads' data.</li>
        <li>We don't share your data with advertising networks or data brokers.</li>
        <li>We don't use your customer data to train AI models.</li>
        <li>We don't read the contents of your leads, notes, or files — except when you
          explicitly ask our support team to look at something to resolve a ticket.</li>
      </ul>

      <h2>Where your data lives &amp; how we protect it</h2>
      <p>
        Data is stored on managed PostgreSQL infrastructure (Supabase, on AWS) in the
        Mumbai region (ap-south-1) by default. We use encryption in transit (TLS 1.2+) and
        at rest (AES-256), hash passwords, isolate every workspace so tenants can't see
        each other's data, and take automated daily backups.
      </p>
      <p>
        To run the service we rely on a few trusted providers — for example Supabase and
        Railway (hosting), Vercel (website), Stripe (payments), and your chosen email
        provider. Optional integrations you enable (WhatsApp, Google, Zoom) receive only
        the data needed to work.
      </p>

      <h2>Your choices</h2>
      <p>You can, at any time:</p>
      <ul>
        <li><b>Access &amp; export</b> your data — most of it is downloadable as CSV directly
          in the product.</li>
        <li><b>Correct</b> information — edit it in the app, or email us for fixed fields.</li>
        <li><b>Delete</b> data — remove leads permanently from Trash, or close your account
          from Settings → Billing.</li>
        <li><b>Withdraw consent</b> for product-update emails via the unsubscribe link.</li>
      </ul>

      <h2>How long we keep it</h2>
      <ul>
        <li>While your account is open, for as long as you use it.</li>
        <li>After you close your account, we delete your data within 30 days (a short window
          in case the request was accidental).</li>
        <li>Billing records are kept longer where tax law requires.</li>
      </ul>

      <h2>Children</h2>
      <p>
        {COMPANY_NAME} is meant for business use and is not intended for anyone under 18. If
        you believe a minor created an account, email{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> and we'll remove it.
      </p>

      <h2>Contact</h2>
      <p>
        For any privacy question or request, email{' '}
        <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>.<br />
        {COMPANY_LEGAL}. {COMPANY_ADDRESS}
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy as the product and law evolve. If a change is material,
        we'll email account admins before it takes effect. The date at the top always shows
        the current version.
      </p>
    </LegalLayout>
  );
}

// ── Terms of Service ────────────────────────────────────────────────────────

export function TermsOfServicePage() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The agreement between you and BrixLead. Short and plain — please read before subscribing."
    >
      <p>
        These Terms are the agreement between {COMPANY_LEGAL} ({COMPANY_NAME}, "we", "our")
        and you, the person or business using {COMPANY_NAME}. By creating an account, you
        agree to them. If you don't agree, please don't use the service.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>You must be 18 or older and able to enter into a contract.</li>
        <li>Give accurate information and keep it current.</li>
        <li>You're responsible for everything that happens under your workspace, including
          by people you invite.</li>
        <li>Keep your password and API keys secure. Tell us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you suspect unauthorized
          access.</li>
      </ul>

      <h2>Acceptable use</h2>
      <p>You agree not to use {COMPANY_NAME} to:</p>
      <ul>
        <li>Store or message people whose data you obtained unlawfully (scraped or purchased
          lists, no lawful basis).</li>
        <li>Send spam or anything that breaks anti-spam or messaging laws.</li>
        <li>Send phishing, malware, or illegal, hateful, or explicit content.</li>
        <li>Reverse-engineer the service, or try to access another workspace or our
          infrastructure.</li>
        <li>Resell or rebrand the service without a written agreement.</li>
      </ul>
      <p>Serious or repeated violations may lead to suspension or account termination.</p>

      <h2>Your data</h2>
      <p>
        Your data is yours. We claim no ownership of it and only process it to run the
        service for you. We don't use it to train AI models. How we handle personal data is
        described in our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>Plans, billing &amp; cancellation</h2>
      <ul>
        <li>The <b>Free</b> plan is genuinely free — no card, no time limit. Paid plans
          simply raise your leads and team limits.</li>
        <li>Paid plans are billed in advance for the chosen period. Prices are listed at{' '}
          <a href="/pricing">brixlead.com/pricing</a> and are exclusive of applicable taxes.</li>
        <li>You can <b>cancel anytime</b> from Settings → Billing. Cancellation stops future
          charges; your paid plan stays active until the end of the current period, then your
          account returns to Free — your data stays intact.</li>
        <li>Fees already paid for a period are not refunded, except where the law requires it.</li>
        <li>Some features (WhatsApp, AI, voice, SMS) use provider accounts and keys you
          connect. Those providers bill you directly for their usage — that's outside our
          billing.</li>
      </ul>

      <h2>Service "as is"</h2>
      <p>
        We work hard to keep {COMPANY_NAME} reliable and will provide it with reasonable
        care and skill. Beyond that, the service is provided "as is" without other
        warranties. We aren't liable for indirect or consequential losses (such as lost
        profits or lost data), and our total liability is limited to the fees you paid us in
        the 12 months before the issue arose. Nothing here limits liability that can't be
        limited by law.
      </p>

      <h2>Termination</h2>
      <ul>
        <li>You can stop using the service and close your account anytime.</li>
        <li>We may suspend or terminate accounts that seriously breach these Terms.</li>
        <li>After termination you can export your data for 30 days, after which we delete it.</li>
      </ul>

      <h2>Governing law</h2>
      <p>
        These Terms are governed by the laws of India. We'll try to resolve any dispute
        through good-faith discussion first.
      </p>

      <h2>Changes &amp; contact</h2>
      <p>
        We may update these Terms as the product evolves; if a change is material, we'll
        email account admins before it takes effect. Questions? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}
